import {
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { RHFTextField } from 'src/components/hook-form';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { addProduct } from 'src/services/products.service';

export default function ClinicAddProductView() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const schema = Yup.object().shape({
    product_name: Yup.string().required(),
    product_code: Yup.string().nullable(),
    description: Yup.string().nullable(),
    price: Yup.number().min(0).required(),
    quantity: Yup.string().required(),
    category: Yup.string().nullable(),
    best_before: Yup.date().nullable(),
    is_active: Yup.boolean(),
    prescription_required: Yup.boolean(),
    image_url: Yup.array().of(Yup.string().nullable()),

  });

  const defaultValues = {
    product_name: '',
    product_code: '',
    description: '',
    price: null,
    quantity: '',
    category: '',
    best_before: null,
    is_active: false,
    prescription_required: false,
    image_url: [],

  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { handleSubmit, reset, watch, setValue } = methods;

  const productImages = watch('image_url');
  const fileName = `product_image_${new Date().toISOString()}`;
  const folderName = `images/products/${user.clinic_id}`;

  const onSubmit = async (data) => {
    try {
      let imageUrls = [];

      if (Array.isArray(data.image_url)) {
        imageUrls = data.image_url;
      } else if (data.image_url) {
        imageUrls = [data.image_url];
      }

      await new Promise((resolve) => {
        const checkImages = setInterval(() => {
          if (imageUrls.length === productImages.length) {
            clearInterval(checkImages);
            resolve();
          }
        }, 100);
      });

      const payload = {
        ...data,
        clinic_id: user.clinic_id,
        image_url: imageUrls,
      };

      await addProduct(payload);

      reset();
      setSnackbarMessage('Product created successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate(-1);
    } catch (err) {
      setSnackbarMessage(err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <Container sx={{ background: { lg: 'white' }, margin: 4, padding: 3, maxWidth: 840, borderRadius: 4 }}>
      <Stack direction="row" gap="14px" alignItems="center" sx={{ marginBottom: '14px' }}>
        <IconButton size="large" onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Create Product</Typography>
      </Stack>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={{ xs: '14px', lg: '44px' }}>
            <Grid item xs={12} lg={6}>
              <RHFTextField name="product_name" label="Product Name" required margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="product_code" label="Product Code (Optional)" margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="price" label="Price" type="number" required margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="quantity" label="Quantity" margin="dense" required/>
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="category" label="Category" margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField
                name="best_before"
                label="Best Before"
                type="date"
                InputLabelProps={{ shrink: true }}
                margin="dense"
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('prescription_required')}
                    onChange={(e) => setValue('prescription_required', e.target.checked)}
                  />
                }
                label="Prescription Required"
              />
            </Grid>

            <Grid item xs={12}>
              <RHFTextField name="description" label="Description" multiline rows={4} margin="dense" />
            </Grid>

            <Grid item xs={12}>
              <RHFImageUpload
                initialImage={productImages}
                fileName={fileName}
                setValue={setValue}
                name="image_url"
                folderName={folderName}
                displayImages
                multipleFiles
                maxFiles={5}
                label="Product Images"
                width={300}
                height={200}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  boxShadow: 10,
                  fontSize: { lg: '16px', xs: '12px' },
                  background: '#D92D7F',
                  ':hover': { background: '#D92D7F' },
                }}
              >
                Save Product
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
